import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './components/Auth/AuthContext';
import InactivityDetector from './components/Auth/InactivityDetector';
import NotificationCenter from './components/CRM/NotificationCenter';
import { CookieProvider } from './contexts/CookieContext';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import { ADMIN_PATH } from './config/adminPath';
import Header from './components/Header';
import Footer from './components/Footer';
import BrandSwitcher from './components/BrandSwitcher';
import CookieConsent from './components/CookieConsent';
import CookiePreferences from './components/CookiePreferences';
import PWAInstallPrompt, { usePWARegistration } from './components/PWA/PWAInstallPrompt';
import MaintenanceBanner from './components/MaintenanceBanner';
import SandboxBanner from './components/common/SandboxBanner';
import SEOHead from './components/SEO/SEOHead';
import MatomoTracker from './components/Analytics/MatomoTracker';
// import DeviceApprovalModal from './components/Auth/DeviceApprovalModal';  // DÉSACTIVÉ
// import { initDeviceFingerprint } from './utils/deviceFingerprint';  // DÉSACTIVÉ
import Home from './pages/Home';
import HomeDNA from './pages/HomeDNA';
import brand from './config/brand';
import About from './pages/About';
import Contact from './pages/Contact';
import LoginPage from './pages/Auth/LoginPage';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import TwoFactorVerificationPage from './pages/Auth/TwoFactorVerificationPage';
import ValidateAccess from './pages/Auth/ValidateAccess';
import ActivatePartnerCollaborator from './pages/Auth/ActivatePartnerCollaborator';
import ConfirmEmailChange from './pages/Auth/ConfirmEmailChange';
import RGPD from './pages/Legal/RGPD';
import Cookies from './pages/Legal/Cookies';
import InformationsLegales from './pages/Legal/InformationsLegales';
import Team from './pages/Team';
import Solutions from './pages/Solutions/Solutions';
import SolutionsParticuliers from './pages/Solutions/SolutionsParticuliers';
import SolutionsProfessionnels from './pages/Solutions/SolutionsProfessionnels';
import SolutionsExpatries from './pages/Solutions/SolutionsExpatries';
import SolutionsVoyage from './pages/Solutions/SolutionsVoyage';
import SolutionsAssistance from './pages/Solutions/SolutionsAssistance';
import CreatePassword from './pages/CreatePassword';
import ActivateCollaborator from './pages/ActivateCollaborator';
import InstallAdminApp from './pages/InstallAdminApp';
import InstallCourtierApp from './pages/InstallCourtierApp';
import InstallAssisteurApp from './pages/InstallAssisteurApp';
import InstallClientApp from './pages/InstallClientApp';
import AppointmentBooking from './components/Public/AppointmentBooking';
import PublicDevisPage from './pages/PublicDevisPage';
import DebugInfo from './pages/DebugInfo';
import PitboardSitePage from './pages/PitboardSite/PitboardSitePage';
import LegalPage from './pages/PitboardSite/LegalPage';
import AIDiscoveryPage from './pages/PitboardSite/AIDiscoveryPage';
import './App.css';
import logger from './utils/logger';

// Recharge la page UNE seule fois si un chunk lazy échoue à charger (cas classique :
// index.html en cache navigateur qui référence d'anciens chunks supprimés après un
// déploiement -> 404 -> fallback HTML -> "Unexpected token '<'"). Évite le white-screen.
const lazyWithRetry = (factory) => lazy(async () => {
  const KEY = 'chunk_reload_once';
  try {
    const mod = await factory();
    sessionStorage.removeItem(KEY);
    return mod;
  } catch (err) {
    if (!sessionStorage.getItem(KEY)) {
      sessionStorage.setItem(KEY, '1');
      window.location.reload();
      return new Promise(() => {}); // suspend le temps du rechargement
    }
    throw err;
  }
});

// Pages lourdes chargées à la demande (code-splitting — VAGUE 4 / 7-H)
const FormBuilderPage = lazyWithRetry(() => import('./pages/FormBuilderPage'));
const AdminDashboard = lazyWithRetry(() => import('./pages/Dashboard/AdminDashboard'));
const ClientDashboard = lazyWithRetry(() => import('./pages/Dashboard/ClientDashboard'));
const PartnerDashboard = lazyWithRetry(() => import('./pages/Dashboard/PartnerDashboard'));
const BrokoliDashboard = lazyWithRetry(() => import('./pages/Dashboard/BrokoliDashboard'));
const BrokoliLoginPage = lazyWithRetry(() => import('./pages/Auth/BrokoliLoginPage'));
const AssisteurDashboard = lazyWithRetry(() => import('./pages/Dashboard/AssisteurDashboard'));
const AssisteurLoginPage = lazyWithRetry(() => import('./pages/Auth/AssisteurLoginPage'));
const AssistanceClientDashboard = lazyWithRetry(() => import('./pages/Dashboard/AssistanceClientDashboard'));
const AssistanceClientLoginPage = lazyWithRetry(() => import('./pages/Auth/AssistanceClientLoginPage'));
const CalendarWindow = lazyWithRetry(() => import('./pages/CalendarWindow'));
const MailWindow = lazyWithRetry(() => import('./pages/MailWindow'));
const ComposeEmailWindow = lazyWithRetry(() => import('./pages/ComposeEmailWindow'));
const CRMClientsWindow = lazyWithRetry(() => import('./pages/CRMClientsWindow'));
const CRMCourtiersWindow = lazyWithRetry(() => import('./pages/CRMCourtiersWindow'));
const ClientDetailWindow = lazyWithRetry(() => import('./pages/ClientDetailWindow'));
const HubDevisWindow = lazyWithRetry(() => import('./pages/HubDevisWindow'));
const HubMessagerieWindow = lazyWithRetry(() => import('./pages/HubMessagerieWindow'));
const ClientMailView = lazyWithRetry(() => import('./pages/ClientMailView'));
const SearchStandaloneWindow = lazyWithRetry(() => import('./pages/SearchStandaloneWindow'));

// Composant pour gérer l'enregistrement PWA selon la route
const PWAManager = () => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  
  // Enregistrer le bon Service Worker
  usePWARegistration(isAdmin);
  
  // Afficher le prompt d'installation (seulement si pas en mode standalone)
  return <PWAInstallPrompt isAdmin={isAdmin} />;
};

// Remonte en haut de page à chaque changement de route (ex: liens du footer).
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Temporary placeholder components

const RendezVous = () => (
  <div className="min-h-screen bg-white flex items-center justify-center">
    <h1 className="text-3xl font-bold text-[#4A4A4A]">Prendre Rendez-vous</h1>
  </div>
);

const ClientPortal = () => (
  <div className="min-h-screen bg-white flex items-center justify-center">
    <h1 className="text-3xl font-bold text-[#4A4A4A]">Portail Client</h1>
  </div>
);

const CourtierPortal = () => (
  <div className="min-h-screen bg-white flex items-center justify-center">
    <h1 className="text-3xl font-bold text-[#4A4A4A]">Portail Courtier</h1>
  </div>
);

function App() {
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [deviceData, setDeviceData] = useState(null);

  // ─── Mode vitrine standalone — pitboardsuite.com / pitboardsuite.fr ──────────
  // Activé à la compilation via : REACT_APP_VITRINE_MODE=true yarn build
  // Seules les 3 routes de la vitrine sont exposées, toutes les routes CRM sont absentes.
  if (process.env.REACT_APP_VITRINE_MODE === 'true') {
    return (
      <Router>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/"        element={<PitboardSitePage />} />
          <Route path="/legal"   element={<LegalPage />} />
          <Route path="/ia-info" element={<AIDiscoveryPage />} />
          <Route path="*"        element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    );
  }
  // ─────────────────────────────────────────────────────────────────────────────
  // useEffect(() => {
  //   initDeviceFingerprint().then(fp => {
  //     logger.debug('📱 Device fingerprint initialized:', fp.fingerprint.substring(0, 16) + '...');
  //   }).catch(err => {
  //     logger.error('Error initializing device fingerprint:', err);
  //   });
  //   
  //   const handleDeviceNotAuthorized = (event) => {
  //     setDeviceData(event.detail);
  //     setShowDeviceModal(true);
  //   };
  //   
  //   window.addEventListener('deviceNotAuthorized', handleDeviceNotAuthorized);
  //   
  //   return () => {
  //     window.removeEventListener('deviceNotAuthorized', handleDeviceNotAuthorized);
  //   };
  // }, []);
  
  return (
    <ErrorBoundary>
      <CookieProvider>
        <Router>
          <ScrollToTop />
          <Toaster richColors position="top-right" />
          <MaintenanceBanner />
          <SandboxBanner />
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-white"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-800" /></div>}>
          <Routes>
            {/* Routes standalone (hors AuthProvider) */}
            <Route path="/calendar-window" element={<CalendarWindow />} />
            
            {/* Route publique pour accéder aux devis via token */}
            <Route path="/devis/:token" element={<PublicDevisPage />} />
            
            {/* Route publique pour activation collaborateur courtier */}
            <Route path="/activate-partner-collaborator" element={<ActivatePartnerCollaborator />} />

            {/* Route publique de confirmation de changement d'email admin */}
            <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />
            
            {/* Routes standalone avec AuthProvider */}
            <Route path="/mail-window" element={
              <AuthProvider>
                <ProtectedRoute>
                  <MailWindow />
                </ProtectedRoute>
              </AuthProvider>
            } />
            
            {/* Fenêtre de composition d'email en popup */}
            <Route path="/admin/mail/compose" element={
              <ComposeEmailWindow />
            } />
            
            <Route path="/crm-clients-window" element={
              <AuthProvider>
                <ProtectedRoute>
                  <CRMClientsWindow />
                </ProtectedRoute>
              </AuthProvider>
            } />
            
            <Route path="/client-detail-window" element={
              <AuthProvider>
                <ProtectedRoute>
                  <ClientDetailWindow />
                </ProtectedRoute>
              </AuthProvider>
            } />
            
            <Route path="/crm-courtiers-window" element={
              <AuthProvider>
                <ProtectedRoute>
                  <CRMCourtiersWindow />
                </ProtectedRoute>
              </AuthProvider>
            } />
            
            <Route path="/hub-devis-window" element={
              <AuthProvider>
                <ProtectedRoute>
                  <HubDevisWindow />
                </ProtectedRoute>
              </AuthProvider>
            } />
            
            <Route path="/hub-messagerie-window" element={
              <AuthProvider>
                <ProtectedRoute>
                  <HubMessagerieWindow />
                </ProtectedRoute>
              </AuthProvider>
            } />
            
            <Route path="/client-mail-view" element={
              <AuthProvider>
                <ProtectedRoute>
                  <ClientMailView />
                </ProtectedRoute>
              </AuthProvider>
            } />

            {/* Fenêtre standalone de recherche globale */}
            <Route path="/search-window" element={
              <AuthProvider>
                <ProtectedRoute>
                  <SearchStandaloneWindow />
                </ProtectedRoute>
              </AuthProvider>
            } />
            
            <Route path="/debug-info" element={<DebugInfo />} />
            
            {/* Site vitrine PitBoard Suite — standalone, sans AuthProvider */}
            <Route path="/pitboard-suite" element={<PitboardSitePage />} />
            <Route path="/pitboard-suite/legal" element={<LegalPage />} />
            <Route path="/pitboard-suite/ia-info" element={<AIDiscoveryPage />} />
            
            {/* Toutes les autres routes avec AuthProvider */}
            <Route path="/*" element={
              <AuthProvider>
                <>
                  <InactivityDetector />
                  <NotificationCenter />
                  <PWAManager />
                  <MatomoTracker />
                  {process.env.NODE_ENV !== 'production' && <BrandSwitcher />}
                  <div className="App min-h-screen flex flex-col">
                  <Routes>
                    {/* Special routes without header/footer */}
                    <Route path="/validate-access" element={<ValidateAccess />} />
                    <Route path="/creer-mot-de-passe" element={<CreatePassword />} />
                    <Route path="/activation" element={<ActivateCollaborator />} />
                    <Route path="/verify-2fa" element={<TwoFactorVerificationPage />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />

                    {/* Espace Brokoli (ASAR uniquement) — sans header/footer public */}
                    <Route path="/brokoli" element={<BrokoliLoginPage />} />
                    <Route path="/brokoli-dashboard" element={
                      <ProtectedRoute requiredRole="brokoli">
                        <BrokoliDashboard />
                      </ProtectedRoute>
                    } />

                    {/* Espace Assisteur (DNA) — sans header/footer public */}
                    <Route path="/assisteur" element={<AssisteurLoginPage />} />
                    <Route path="/assisteur-dashboard" element={
                      <ProtectedRoute requiredRole="assisteur">
                        <AssisteurDashboard />
                      </ProtectedRoute>
                    } />

                    {/* Espace Client d'assistance (DNA) — sans header/footer public */}
                    <Route path="/assistance-client" element={<AssistanceClientLoginPage />} />
                    <Route path="/assistance-client-dashboard" element={
                      <ProtectedRoute requiredRole="assistance_client">
                        <AssistanceClientDashboard />
                      </ProtectedRoute>
                    } />
              
                    {/* All other routes with header/footer */}
                    <Route path="/*" element={
                <>
                  <Header />
                  <SEOHead />
                  <main className="flex-1">
                    <Routes>
                      {/* Public routes */}
                      <Route path="/" element={brand.key === 'dna' ? <HomeDNA /> : <Home />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/team" element={<Team />} />
                      <Route path="/solutions" element={<Solutions />} />
                      <Route path="/solutions/particuliers" element={<SolutionsParticuliers />} />
                      <Route path="/solutions/professionnels" element={<SolutionsProfessionnels />} />
                      <Route path="/solutions/expatries" element={<SolutionsExpatries />} />
                      <Route path="/solutions/voyage" element={<SolutionsVoyage />} />
                      <Route path="/solutions/assistance" element={<SolutionsAssistance />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/rendez-vous" element={<AppointmentBooking backendUrl={process.env.REACT_APP_BACKEND_URL} />} />
                      {/* ⚠️ Chemin admin SECRET — défini dans REACT_APP_ADMIN_PATH */}
                      <Route path={`/${ADMIN_PATH}`} element={<LoginPage />} />
                      {/* /login reste disponible pour les espaces client et courtier */}
                      <Route path="/login" element={<LoginPage />} />
                      
                      {/* Legal pages */}
                      <Route path="/informations-legales" element={<InformationsLegales />} />
                      <Route path="/rgpd" element={<RGPD />} />
                      <Route path="/cookies" element={<Cookies />} />
                      
                      {/* Page d'installation de l'app Admin */}
                      <Route path="/install-admin" element={<InstallAdminApp />} />
                      
                      {/* Page d'installation de l'app Courtier */}
                      <Route path="/install-courtier" element={<InstallCourtierApp />} />
                      
                      {/* Page d'installation de l'app Client */}
                      <Route path="/install-client" element={<InstallClientApp />} />

                      {/* Page d'installation de l'app Assisteur */}
                      <Route path="/install-assisteur" element={<InstallAssisteurApp />} />
                      
                      {/* Form Builder (standalone page) */}
                      <Route path="/crm/form-builder" element={
                        <ProtectedRoute requiredSpace="admin">
                          <FormBuilderPage />
                        </ProtectedRoute>
                      } />
                      
                      {/* Protected routes */}
                      <Route path="/admin" element={
                        <ProtectedRoute requiredSpace="admin">
                          <AdminDashboard />
                        </ProtectedRoute>
                      } />
                      
                      <Route path="/client-dashboard" element={
                        <ProtectedRoute requiredSpace="client">
                          <ClientDashboard />
                        </ProtectedRoute>
                      } />
                      
                      <Route path="/courtier-dashboard" element={
                        <ProtectedRoute requiredSpace="partner">
                          <PartnerDashboard />
                        </ProtectedRoute>
                      } />
                    </Routes>
                  </main>
                  <Footer />
                </>
                  } />
                  </Routes>
                  
                  {/* Cookie Components */}
                  <CookieConsent />
                  <CookiePreferences />
                  
                  {/* Device Approval Modal - DÉSACTIVÉ */}
                  {/* {showDeviceModal && (
                    <DeviceApprovalModal 
                      deviceData={deviceData}
                      onClose={() => setShowDeviceModal(false)}
                    />
                  )} */}
                </div>
                </>
              </AuthProvider>
            } />
          </Routes>
          </Suspense>
        </Router>
      </CookieProvider>
    </ErrorBoundary>
  );
}

export default App;